import * as THREE from 'three';

// Portal positions
const START_PORTAL_POSITION = new THREE.Vector3(500, 10, 600); // Changed y from 0 to -5
const EXIT_PORTAL_POSITION = new THREE.Vector3(-70, 10, -700); // Changed y from 200 to -5
const VIBE_METAVERSE_PORTAL_POSITION = new THREE.Vector3(40, 10, -700); // Position near exit portal

export class PortalManager {
    constructor(scene) {
        this.scene = scene;
        this.startPortalGroup = null;
        this.exitPortalGroup = null;
        this.vibeMetaversePortalGroup = null;
        this.exitPortalBox = null;
        this.vibeMetaversePortalBox = null;
    }

    createStartPortal() {
        // Create portal group to contain all portal elements
        this.startPortalGroup = new THREE.Group();
        this.startPortalGroup.position.set(START_PORTAL_POSITION.x, START_PORTAL_POSITION.y, START_PORTAL_POSITION.z);
        this.startPortalGroup.rotation.x = 0;
        this.startPortalGroup.rotation.y = 0;

        // Create portal effect
        const startPortalGeometry = new THREE.TorusGeometry(25, 3, 16, 100);
        const startPortalMaterial = new THREE.MeshPhongMaterial({
            color: 0xff0000,
            emissive: 0xff0000,
            transparent: true,
            opacity: 0.8
        });
        const startPortal = new THREE.Mesh(startPortalGeometry, startPortalMaterial);
        this.startPortalGroup.add(startPortal);

        // Create portal inner surface
        const startPortalInnerGeometry = new THREE.CircleGeometry(22, 32);
        const startPortalInnerMaterial = new THREE.MeshBasicMaterial({
            color: 0xff0000,
            transparent: true,
            opacity: 0.5,
            side: THREE.DoubleSide
        });
        const startPortalInner = new THREE.Mesh(startPortalInnerGeometry, startPortalInnerMaterial);
        this.startPortalGroup.add(startPortalInner);

        // Create particle system for portal effect
        const startPortalParticleCount = 1000;
        const startPortalParticles = new THREE.BufferGeometry();
        const startPortalPositions = new Float32Array(startPortalParticleCount * 3);
        const startPortalColors = new Float32Array(startPortalParticleCount * 3);

        for (let i = 0; i < startPortalParticleCount * 3; i += 3) {
            const angle = Math.random() * Math.PI * 2;
            const radius = 25 + (Math.random() - 0.5) * 6;
            startPortalPositions[i] = Math.cos(angle) * radius;
            startPortalPositions[i + 1] = Math.sin(angle) * radius;
            startPortalPositions[i + 2] = (Math.random() - 0.5) * 4;

            startPortalColors[i] = 0.8 + Math.random() * 0.2;
            startPortalColors[i + 1] = 0;
            startPortalColors[i + 2] = 0;
        }

        startPortalParticles.setAttribute('position', new THREE.BufferAttribute(startPortalPositions, 3));
        startPortalParticles.setAttribute('color', new THREE.BufferAttribute(startPortalColors, 3));

        const startPortalParticleMaterial = new THREE.PointsMaterial({
            size: 0.2,
            vertexColors: true,
            transparent: true,
            opacity: 0.6
        });

        const startPortalParticleSystem = new THREE.Points(startPortalParticles, startPortalParticleMaterial);
        this.startPortalGroup.add(startPortalParticleSystem);

        // Add portal group to scene
        this.scene.add(this.startPortalGroup);

        // Store particle system for animation
        this.startPortalParticles = startPortalParticles;
        this.startPortalInnerMaterial = startPortalInnerMaterial;
    }

    createExitPortal() {
        // Create portal group to contain all portal elements
        this.exitPortalGroup = new THREE.Group();
        this.exitPortalGroup.position.set(EXIT_PORTAL_POSITION.x, EXIT_PORTAL_POSITION.y, EXIT_PORTAL_POSITION.z);
        this.exitPortalGroup.rotation.x = 0;
        this.exitPortalGroup.rotation.y = 0;

        // Create portal effect
        const exitPortalGeometry = new THREE.TorusGeometry(25, 3, 16, 100);
        const exitPortalMaterial = new THREE.MeshPhongMaterial({
            color: 0x00ff00,
            emissive: 0x00ff00,
            transparent: true,
            opacity: 0.8
        });
        const exitPortal = new THREE.Mesh(exitPortalGeometry, exitPortalMaterial);
        this.exitPortalGroup.add(exitPortal);

        // Create portal inner surface
        const exitPortalInnerGeometry = new THREE.CircleGeometry(22, 32);
        const exitPortalInnerMaterial = new THREE.MeshBasicMaterial({
            color: 0x00ff00,
            transparent: true,
            opacity: 0.5,
            side: THREE.DoubleSide
        });
        const exitPortalInner = new THREE.Mesh(exitPortalInnerGeometry, exitPortalInnerMaterial);
        this.exitPortalGroup.add(exitPortalInner);

        // Add portal label
        const loader = new THREE.TextureLoader();
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = 512;
        canvas.height = 64;
        context.fillStyle = '#00ff00';
        context.font = 'bold 32px Arial';
        context.textAlign = 'center';
        context.fillText('VIBEVERSE PORTAL', canvas.width / 2, canvas.height / 2);
        const texture = new THREE.CanvasTexture(canvas);
        const labelGeometry = new THREE.PlaneGeometry(30, 5);
        const labelMaterial = new THREE.MeshBasicMaterial({
            map: texture,
            transparent: true,
            side: THREE.DoubleSide
        });
        const label = new THREE.Mesh(labelGeometry, labelMaterial);
        label.position.y = 30;
        this.exitPortalGroup.add(label);

        // Create particle system for portal effect
        const exitPortalParticleCount = 1000;
        const exitPortalParticles = new THREE.BufferGeometry();
        const exitPortalPositions = new Float32Array(exitPortalParticleCount * 3);
        const exitPortalColors = new Float32Array(exitPortalParticleCount * 3);

        for (let i = 0; i < exitPortalParticleCount * 3; i += 3) {
            const angle = Math.random() * Math.PI * 2;
            const radius = 25 + (Math.random() - 0.5) * 6;
            exitPortalPositions[i] = Math.cos(angle) * radius;
            exitPortalPositions[i + 1] = Math.sin(angle) * radius;
            exitPortalPositions[i + 2] = (Math.random() - 0.5) * 4;

            exitPortalColors[i] = 0;
            exitPortalColors[i + 1] = 0.8 + Math.random() * 0.2;
            exitPortalColors[i + 2] = 0;
        }

        exitPortalParticles.setAttribute('position', new THREE.BufferAttribute(exitPortalPositions, 3));
        exitPortalParticles.setAttribute('color', new THREE.BufferAttribute(exitPortalColors, 3));

        const exitPortalParticleMaterial = new THREE.PointsMaterial({
            size: 0.2,
            vertexColors: true,
            transparent: true,
            opacity: 0.6
        });

        const exitPortalParticleSystem = new THREE.Points(exitPortalParticles, exitPortalParticleMaterial);
        this.exitPortalGroup.add(exitPortalParticleSystem);

        // Add portal group to scene
        this.scene.add(this.exitPortalGroup);

        // Create portal collision box
        this.exitPortalBox = new THREE.Box3().setFromObject(this.exitPortalGroup);

        // Store particle system for animation
        this.exitPortalParticles = exitPortalParticles;
        this.exitPortalInnerMaterial = exitPortalInnerMaterial;
    }

    createVibeMetaversePortal() {
        // Create portal group to contain all portal elements
        this.vibeMetaversePortalGroup = new THREE.Group();
        this.vibeMetaversePortalGroup.position.set(VIBE_METAVERSE_PORTAL_POSITION.x, VIBE_METAVERSE_PORTAL_POSITION.y, VIBE_METAVERSE_PORTAL_POSITION.z);
        this.vibeMetaversePortalGroup.rotation.x = 0;
        this.vibeMetaversePortalGroup.rotation.y = 0;

        // Create portal effect
        const vibeMetaversePortalGeometry = new THREE.TorusGeometry(25, 3, 16, 100);
        const vibeMetaversePortalMaterial = new THREE.MeshPhongMaterial({
            color: 0x800080, // Violet color
            emissive: 0x800080,
            transparent: true,
            opacity: 0.8
        });
        const vibeMetaversePortal = new THREE.Mesh(vibeMetaversePortalGeometry, vibeMetaversePortalMaterial);
        this.vibeMetaversePortalGroup.add(vibeMetaversePortal);

        // Create portal inner surface
        const vibeMetaversePortalInnerGeometry = new THREE.CircleGeometry(22, 32);
        const vibeMetaversePortalInnerMaterial = new THREE.MeshBasicMaterial({
            color: 0x800080,
            transparent: true,
            opacity: 0.5,
            side: THREE.DoubleSide
        });
        const vibeMetaversePortalInner = new THREE.Mesh(vibeMetaversePortalInnerGeometry, vibeMetaversePortalInnerMaterial);
        this.vibeMetaversePortalGroup.add(vibeMetaversePortalInner);

        // Add portal label
        const loader = new THREE.TextureLoader();
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = 512;
        canvas.height = 64;
        context.fillStyle = '#800080';
        context.font = 'bold 32px Arial';
        context.textAlign = 'center';
        context.fillText('THE VIBE METAVERSE', canvas.width / 2, canvas.height / 2);
        const texture = new THREE.CanvasTexture(canvas);
        const labelGeometry = new THREE.PlaneGeometry(30, 5);
        const labelMaterial = new THREE.MeshBasicMaterial({
            map: texture,
            transparent: true,
            side: THREE.DoubleSide
        });
        const label = new THREE.Mesh(labelGeometry, labelMaterial);
        label.position.y = 30;
        this.vibeMetaversePortalGroup.add(label);

        // Create particle system for portal effect
        const vibeMetaversePortalParticleCount = 1000;
        const vibeMetaversePortalParticles = new THREE.BufferGeometry();
        const vibeMetaversePortalPositions = new Float32Array(vibeMetaversePortalParticleCount * 3);
        const vibeMetaversePortalColors = new Float32Array(vibeMetaversePortalParticleCount * 3);

        for (let i = 0; i < vibeMetaversePortalParticleCount * 3; i += 3) {
            const angle = Math.random() * Math.PI * 2;
            const radius = 25 + (Math.random() - 0.5) * 6;
            vibeMetaversePortalPositions[i] = Math.cos(angle) * radius;
            vibeMetaversePortalPositions[i + 1] = Math.sin(angle) * radius;
            vibeMetaversePortalPositions[i + 2] = (Math.random() - 0.5) * 4;

            vibeMetaversePortalColors[i] = 0.5 + Math.random() * 0.2; // Violet R component
            vibeMetaversePortalColors[i + 1] = 0; // Violet G component
            vibeMetaversePortalColors[i + 2] = 0.5 + Math.random() * 0.2; // Violet B component
        }

        vibeMetaversePortalParticles.setAttribute('position', new THREE.BufferAttribute(vibeMetaversePortalPositions, 3));
        vibeMetaversePortalParticles.setAttribute('color', new THREE.BufferAttribute(vibeMetaversePortalColors, 3));

        const vibeMetaversePortalParticleMaterial = new THREE.PointsMaterial({
            size: 0.2,
            vertexColors: true,
            transparent: true,
            opacity: 0.6
        });

        const vibeMetaversePortalParticleSystem = new THREE.Points(vibeMetaversePortalParticles, vibeMetaversePortalParticleMaterial);
        this.vibeMetaversePortalGroup.add(vibeMetaversePortalParticleSystem);

        // Add portal group to scene
        this.scene.add(this.vibeMetaversePortalGroup);

        // Create portal collision box
        this.vibeMetaversePortalBox = new THREE.Box3().setFromObject(this.vibeMetaversePortalGroup);

        // Store particle system for animation
        this.vibeMetaversePortalParticles = vibeMetaversePortalParticles;
        this.vibeMetaversePortalInnerMaterial = vibeMetaversePortalInnerMaterial;
    }

    checkExitPortalCollision(player) {
        if (!this.exitPortalBox || !player) return false;

        const playerBox = new THREE.Box3().setFromObject(player);
        const portalDistance = playerBox.getCenter(new THREE.Vector3()).distanceTo(this.exitPortalBox.getCenter(new THREE.Vector3()));

        if (portalDistance < 50) {
            const currentParams = new URLSearchParams(window.location.search);
            const newParams = new URLSearchParams();
            newParams.append('portal', true);
            console.log("Player name:", window.playerName);
            newParams.append('username', window.playerName || 'Anonymous');
            newParams.append('color', 'white');
            newParams.append('speed', '1');

            for (const [key, value] of currentParams) {
                newParams.append(key, value);
            }
            const paramString = newParams.toString();
            const nextPage = 'https://portal.pieter.com' + (paramString ? '?' + paramString : '');

            // Create hidden iframe to preload next page
            if (!document.getElementById('preloadFrame')) {
                const iframe = document.createElement('iframe');
                iframe.id = 'preloadFrame';
                iframe.style.display = 'none';
                iframe.src = nextPage;
                document.body.appendChild(iframe);
            }

            // Only redirect once actually in the portal
            if (playerBox.intersectsBox(this.exitPortalBox)) {
                window.location.href = nextPage;
                return true;
            }
        }
        return false;
    }

    checkVibeMetaversePortalCollision(player) {
        if (!this.vibeMetaversePortalBox || !player) return false;

        const playerBox = new THREE.Box3().setFromObject(player);
        const portalDistance = playerBox.getCenter(new THREE.Vector3()).distanceTo(this.vibeMetaversePortalBox.getCenter(new THREE.Vector3()));

        if (portalDistance < 50) {
            // Create URL parameters
            const newParams = new URLSearchParams();
            newParams.append('portal', 'true');
            newParams.append('username', window.playerName || 'Anonymous');
            newParams.append('color', 'white');
            newParams.append('speed', '1');
            newParams.append('avatar_url', 'https://oceanfables.com/assets/pirate.glb');

            // Get current URL parameters and add them
            const currentParams = new URLSearchParams(window.location.search);
            for (const [key, value] of currentParams) {
                newParams.append(key, value);
            }

            const paramString = newParams.toString();
            const nextPage = 'https://thevibemetaverse.com' + (paramString ? '?' + paramString : '');

            // Create hidden iframe to preload next page
            if (!document.getElementById('preloadFrame')) {
                const iframe = document.createElement('iframe');
                iframe.id = 'preloadFrame';
                iframe.style.display = 'none';
                iframe.src = nextPage;
                document.body.appendChild(iframe);
            }

            // Only redirect once actually in the portal
            if (playerBox.intersectsBox(this.vibeMetaversePortalBox)) {
                window.location.href = nextPage;
                return true;
            }
        }
        return false;
    }

    animate() {
        // Animate start portal particles
        if (this.startPortalParticles) {
            const positions = this.startPortalParticles.attributes.position.array;
            for (let i = 0; i < positions.length; i += 3) {
                positions[i + 1] += 0.05 * Math.sin(Date.now() * 0.001 + i);
            }
            this.startPortalParticles.attributes.position.needsUpdate = true;
        }

        // Animate exit portal particles
        if (this.exitPortalParticles) {
            const positions = this.exitPortalParticles.attributes.position.array;
            for (let i = 0; i < positions.length; i += 3) {
                positions[i + 1] += 0.05 * Math.sin(Date.now() * 0.001 + i);
            }
            this.exitPortalParticles.attributes.position.needsUpdate = true;
        }

        // Animate Vibe Metaverse portal particles
        if (this.vibeMetaversePortalParticles) {
            const positions = this.vibeMetaversePortalParticles.attributes.position.array;
            for (let i = 0; i < positions.length; i += 3) {
                positions[i + 1] += 0.05 * Math.sin(Date.now() * 0.001 + i);
            }
            this.vibeMetaversePortalParticles.attributes.position.needsUpdate = true;
        }
    }
} 